import React, { forwardRef, useEffect, useState } from 'react';
import { AlertOctagon, CheckCircle2, ShieldAlert, Sparkles } from 'lucide-react';
import { toString as renderQrSvg } from 'qrcode';
import { CharacterDefinition, CHARACTER_MAP, Dimension12D } from '../data/characterRegistry';
import { QuizResult, ResultMetric } from '../types';
import { getUnifiedLifestyleMetrics } from '../utils/lifestyleMetricsEngine';

export interface ShareCardProps {
  character?: CharacterDefinition;
  result?: QuizResult;
  userProfile?: Record<Dimension12D, number>;
  metrics?: ResultMetric[];
  matchRate?: string;
  caseId?: string;
  subtitle?: string;
}

// 正式分享卡固定规格：窄版 1:2.1 竖版海报，手机端预览字号更大
export const SHARE_CARD_WIDTH = 640;
export const SHARE_CARD_HEIGHT = 1350;

// 分享卡二维码统一指向的正式产品地址（微信/小红书扫码直达测试首页）
export const SHARE_QR_TARGET_URL = 'https://quiz.thistest.cn/?from=share';

// 小红书官方账号名（暂定「这也要测」）：正式账号 ID 确定后另加独立配置字段；
// 此处留空字符串则整段隐藏，不展示占位假账号
const SHARE_XHS_ACCOUNT = '这也要测';

/**
 * 专为社交传播（小红书 / 朋友圈）打造的窄版竖排结果卡海报组件
 * 固定 640 × 1350 竖版比例，视觉重心为角色全身大图
 * 8 个生活方式指标单列 8 行纵向排列（指标名+百分比 → 进度条 → 一句短评），提升手机阅读性
 */
export const ShareCard = forwardRef<HTMLDivElement, ShareCardProps>((props, ref) => {
  const {
    character,
    result,
    userProfile,
    metrics,
    matchRate,
    caseId,
    subtitle,
  } = props;

  // 确定生效的角色信息
  const effectiveChar: CharacterDefinition =
    character ||
    (result ? CHARACTER_MAP[result.id] : undefined) || {
      id: result?.id || 'golden_retriever',
      name: result?.targetName || '金毛寻回犬',
      pool: result?.badgeType === 'danger' ? 'abstract' : 'real',
      personaKeywords: ['阳光', '大暖男'],
      image: result?.imageUrl || '/characters/golden_retriever.png',
      coordinates: { C: 5, E: 4, I: 1, S: 2, M: 3, P: 4, V: 4, N: 1, X: 2, A: 1, B: 1, R: 1 },
    };

  const isDanger = effectiveChar.pool === 'abstract';

  // 生成指向正式地址的真实二维码（内联 SVG 矢量形式：html-to-image 导出时任意尺寸渲染都保持锐利，
  // 模块边缘清晰是压缩后仍可扫码的关键；纠错 Q 级 25%，抗小红书/微信二次压缩）
  const [qrSvg, setQrSvg] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    renderQrSvg(SHARE_QR_TARGET_URL, {
      type: 'svg',
      margin: 2, // 二维码自带 2 模块白色静区，配合外层白色边框形成完整留白
      errorCorrectionLevel: 'Q',
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then((svg) => {
        if (cancelled) return;
        // 让 SVG 铺满容器，导出时按 160px 物理尺寸光栅化，模块边缘无缩放模糊
        setQrSvg(svg.replace('<svg ', '<svg style="width:100%;height:100%;display:block" '));
      })
      .catch((err) => console.warn('QR generation failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // 8 个固定生活方式指标
  const effectiveMetrics: ResultMetric[] =
    metrics ||
    (userProfile
      ? getUnifiedLifestyleMetrics(userProfile, effectiveChar)
      : result?.metrics ||
        getUnifiedLifestyleMetrics(effectiveChar.coordinates ? (Object.fromEntries(Object.entries(effectiveChar.coordinates).map(([k, v]) => [k, (v - 1) * 25])) as any) : ({} as any), effectiveChar));


  const effectiveMatchRate = matchRate || result?.matchRate || '96.8%';
  const effectiveCaseId =
    caseId ||
    result?.caseId ||
    `SPECIMEN #${isDanger ? 'ABST' : 'PET'}-${effectiveChar.id.toUpperCase().slice(0, 4)}-2026`;
  const effectiveSubtitle =
    subtitle ||
    result?.subtitle ||
    (isDanger
      ? `超现实高维收容体 · ${effectiveChar.personaKeywords.slice(0, 2).join(' · ')}`
      : `标准碳基伴侣 · ${effectiveChar.personaKeywords.slice(0, 2).join(' · ')}`);
  const stampText = isDanger ? '【绝密收容 · 强制绑缚】' : '【合规准养 · 正式批复】';

  // 提取一句简明扼要、高笑点的短判词
  const getShortVerdict = () => {
    if (isDanger) {
      return `【系统特批】常规碳基物种已无法承受受试者精神负荷，经越权审批，指派「${effectiveChar.name}」接管你的人生。`;
    }
    return `【正式准养】各项生活自理与自律基准审核达标，获颁特许准养资格，正式批复「${effectiveChar.name}」为终身法定伴侣。`;
  };

  return (
    <div
      ref={ref}
      id="share-poster-card"
      style={{ width: `${SHARE_CARD_WIDTH}px`, height: `${SHARE_CARD_HEIGHT}px` }}
      className="bg-[#FAF7F0] border-[6px] border-ink-950 px-6 pt-5 pb-5 flex flex-col justify-between relative font-sans text-ink-900 select-none box-border shadow-2xl shrink-0 overflow-hidden"
    >
      {/* ================= 1. 顶部 Header ================= */}
      <div className="space-y-1.5 border-b-3 border-ink-950 pb-2 shrink-0">
        {/* 微型档案角标 */}
        <div className="flex justify-between items-center text-[12px] font-mono text-ink-400 px-0.5 -mb-0.5">
          <span>SPECIMEN DOSSIER // FORM BIO-2026-X</span>
          <span>ARCHIVE CODE: #FINAL-PUB</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[13px] font-black uppercase text-ink-950 min-w-0">
            <span
              className={`w-3 h-3 inline-block shrink-0 ${
                isDanger ? 'bg-stamp animate-pulse' : 'bg-lab-green'
              }`}
            />
            <span className="truncate">BIO-ANOMALY EVALUATION // 伴侣生物收容鉴定中心</span>
          </div>
          <span
            className={`font-mono text-[12px] font-black px-2 py-0.5 border-2 shrink-0 whitespace-nowrap ${
              isDanger
                ? 'border-stamp text-stamp bg-stamp-light'
                : 'border-ink-700 text-ink-800 bg-paper-200'
            }`}
          >
            {effectiveCaseId}
          </span>
        </div>

        {/* 状态警告横条 */}
        <div
          className={`flex items-center justify-between px-3.5 py-1 text-[14px] font-mono font-bold tracking-wide border-2 ${
            isDanger
              ? 'bg-stamp text-paper-50 border-stamp'
              : 'bg-ink-900 text-paper-50 border-ink-900'
          }`}
        >
          <div className="flex items-center gap-2">
            {isDanger ? (
              <AlertOctagon className="w-4.5 h-4.5 shrink-0" />
            ) : (
              <CheckCircle2 className="w-4.5 h-4.5 shrink-0 text-lab-green" />
            )}
            <span>
              {isDanger
                ? '【高危异常：已排除全部常规碳基动物，启动高维收容】'
                : '【标准评级：通过现实生活方式承载与自理审核】'}
            </span>
          </div>
          <span className="text-[13px] opacity-80">
            {isDanger ? '【高危收容】' : '【合规准养】'}
          </span>
        </div>
      </div>

      {/* ================= 2. 上部：角色名称 + 匹配度 ================= */}
      <div className="flex items-end justify-between shrink-0 px-1">
        <div>
          <div className="text-[12px] font-mono text-ink-500 uppercase tracking-wider">
            SYSTEM DIRECTIVE // 裁定分配物种：
          </div>
          <h1 className="text-4xl font-black tracking-tight text-ink-950 mt-1 leading-none">
            {effectiveChar.name}
          </h1>
          <p className="text-[14px] font-mono text-ink-600 mt-1 line-clamp-1">
            {effectiveSubtitle}
          </p>
        </div>

        <div className="text-right font-mono shrink-0 pl-4">
          <span className="text-[12px] text-ink-500 block">综合匹配契合度</span>
          <span
            className={`text-3xl font-black tracking-tight ${
              isDanger ? 'text-stamp' : 'text-lab-green'
            }`}
          >
            {effectiveMatchRate}
          </span>
        </div>
      </div>

      {/* ================= 3. 中部：角色大图 (核心视觉中心) ================= */}
      <div className="w-full h-[258px] bg-white border-3 border-ink-950 p-4 relative shadow-sharp-sm flex items-center justify-center overflow-hidden shrink-0">
        {/* 标本图视窗角标（置于角色图层之上，避免被不透明图片背景遮盖） */}
        <div className="absolute top-1.5 left-3 text-[11px] font-mono text-ink-300 z-20">
          RENDER: 3D LOW-POLY // ISO-VIEW
        </div>
        <div className="absolute top-1.5 right-3 text-[11px] font-mono text-ink-300 z-20">
          SCALE: 1:1 // LIVING ENTITY
        </div>

        {/* 角色大图：作为最显眼主体 */}
        <img
          src={effectiveChar.image}
          alt={effectiveChar.name}
          crossOrigin="anonymous"
          style={{
            width: 'auto',
            maxWidth: '85%',
            height: '100%',
            maxHeight: '226px',
            objectFit: 'contain',
          }}
          className="z-10 block"
        />

        {/* 右上角红色/蓝色印章 */}
        <div className="absolute top-9 right-4 pointer-events-none z-20">
          <div
            className={`px-3 py-1 border-3 font-black font-mono text-[14px] uppercase tracking-widest rotate-[8deg] shadow-xs ${
              isDanger
                ? 'border-stamp text-stamp bg-white/95 ring-2 ring-stamp/40'
                : 'border-lab-blue text-lab-blue bg-white/95 ring-2 ring-lab-blue/30'
            }`}
          >
            {stampText}
          </div>
        </div>

        {/* 视窗底部标本参数 */}
        <div className="absolute bottom-2 left-3 right-3 z-20 flex justify-between text-[11px] font-mono text-ink-400 border-t border-ink-100 pt-1">
          <span>SPECIMEN: {effectiveChar.id.toUpperCase()}</span>
          <span>STATUS: ACTIVE // CONFIRMED</span>
        </div>
      </div>

      {/* ================= 4. 下部：8 个固定生活方式指标 (单列 8 行纵向阅读) ================= */}
      <div className="border-3 border-ink-950 bg-white px-5 py-3.5 shadow-sharp-sm shrink-0 space-y-3">
        <div className="flex justify-between items-center text-[15px] font-mono font-bold text-ink-900 border-b border-ink-200 pb-1.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-ink-950 inline-block" />
            <span>LIFESTYLE PROFILE // 8维生活方式评估刻度</span>
          </span>
          <span className="text-ink-400 font-normal text-[12px] shrink-0 whitespace-nowrap">VAL / 100%</span>
        </div>

        <div className="space-y-2.5">
          {effectiveMetrics.map((m, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex justify-between items-baseline gap-3 font-mono leading-none">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[19px] font-bold text-ink-900">{m.label}</span>
                  {m.subLabel && (
                    <span className="text-[12px] font-normal text-ink-400 uppercase tracking-wider truncate">
                      {m.subLabel}
                    </span>
                  )}
                </div>
                <span className="font-black text-ink-950 font-mono text-[19px] shrink-0">
                  {m.value}%
                </span>
              </div>

              {/* 进度条 */}
              <div className="w-full h-3 bg-paper-200 border border-ink-400 p-[1px]">
                <div
                  className={`h-full ${isDanger ? 'bg-stamp' : 'bg-lab-blue'}`}
                  style={{ width: `${m.value}%` }}
                />
              </div>

              {/* 一句角色化短评（单行截断） */}
              {m.note && (
                <div className="text-[14px] font-mono text-ink-600 truncate leading-none pt-0.5">
                  {m.note}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ================= 5. 底部：短判词 + 品牌存根 ================= */}
      <div className="space-y-2 shrink-0">
        {/* 一句核心短判词 */}
        <div
          className={`p-3 border-2 text-[15px] font-mono leading-snug flex items-start gap-2.5 ${
            isDanger
              ? 'bg-stamp-light/70 border-stamp text-stamp-dark'
              : 'bg-paper-200/90 border-ink-400 text-ink-800'
          }`}
        >
          {isDanger ? (
            <ShieldAlert className="w-4.5 h-4.5 shrink-0 mt-0.5 text-stamp" />
          ) : (
            <Sparkles className="w-4.5 h-4.5 shrink-0 mt-0.5 text-lab-blue" />
          )}
          <span className="line-clamp-2">{getShortVerdict()}</span>
        </div>

        {/* 品牌署名 + 真实可扫二维码（指向正式测试地址） */}
        <div className="flex items-center justify-between gap-4 border-t-2 border-dashed border-ink-400 pt-2 pb-0.5 text-ink-600">
          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="text-[16px] font-black text-ink-950 tracking-wider leading-none">
              测测你这辈子到底适合养什么东西
            </div>
            <div className="text-[11px] font-mono text-ink-400 leading-snug truncate">
              中央伴侣生物收容鉴定所 · 终审盖印档案
              {SHARE_XHS_ACCOUNT ? ` // XHS: ${SHARE_XHS_ACCOUNT}` : ''}
            </div>
            {/* 扫码引导文案 */}
            <div className="text-[14px] font-bold text-ink-900 leading-snug pt-0.5">
              扫码测测你这辈子适合养什么东西
            </div>
            <div className="text-[10px] font-mono text-ink-400 leading-none">
              OFFICIAL SEAL // VERIFIED: 2026
            </div>
          </div>

          {/* 真实二维码：SVG 矢量 + 白底白边留白。
              尺寸 148px = 37 模块单元 × 4px（版本4+2模块静区），每模块精确整数像素，
              确保 html-to-image 光栅化后网格均匀、压缩后仍可稳定扫码 */}
          <div className="shrink-0 bg-white border-2 border-ink-950 p-2.5 shadow-sharp-sm">
            <div
              style={{ width: '148px', height: '148px' }}
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

ShareCard.displayName = 'ShareCard';
